import React, { useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Button,
} from "@mui/material";
import { Field, useForm } from "react-final-form";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import Video from "./Video";
import History from "./History";

// Exercise-level fields. Empty array = intentional. Old data has type on sets.
const getExerciseFields = (exercise = {}) => {
  if (Array.isArray(exercise.fields)) {
    return exercise.fields.map((t) => String(t).toUpperCase()).slice(0, 2);
  }
  const firstSet = exercise.sets?.[0];
  if (firstSet && (firstSet.type != null || (Array.isArray(firstSet.types) && firstSet.types.length > 0))) {
    if (Array.isArray(firstSet.types) && firstSet.types.length > 0) {
      return firstSet.types.map((t) => String(t).toUpperCase()).slice(0, 2);
    }
    if (firstSet.type === "TIME") return ["TIME"];
    if (firstSet.type === "RPE") return ["RPE", "WEIGHT"];
    return ["WEIGHT", "RPE"];
  }
  return ["WEIGHT", "RPE"];
};

const Exercise = ({ exercise, index, currentWorkoutDate }) => {
  const form = useForm();
  const activeFields = getExerciseFields(exercise);

  const isComplete = (setIndex) => {
    const key = `exercises[${index}].sets[${setIndex}]`;
    const set = exercise.sets?.[setIndex] || {};
    const checks = {
      WEIGHT: form.getFieldState(`${key}.weight`)?.value,
      RPE: form.getFieldState(`${key}.rpe`)?.value,
      TIME: form.getFieldState(`${key}.time`)?.value,
    };
    return activeFields.every((f) => checks[f]);
  };
  const [openHistory, setOpenHistory] = useState(false);

  // Function to handle auto-saving
  const handleAutoSave = () => {
    form.submit(); // Trigger the form submission to save
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography>
          <strong>{exercise.name}</strong>
        </Typography>
        <Button onClick={() => setOpenHistory(true)}>History</Button>
      </Box>
      {openHistory && (
        <History
          exerciseName={exercise.name}
          close={() => setOpenHistory(false)}
          currentWorkoutDate={currentWorkoutDate}
        />
      )}
      <Typography fontSize="small" sx={{ wordBreak: "break-word" }}>
        {exercise.description}
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: "5px" }}>#</TableCell>
            <TableCell align="center">Reps</TableCell>
            {activeFields.includes("WEIGHT") && <TableCell>Weight</TableCell>}
            {activeFields.includes("RPE") && <TableCell>RPE</TableCell>}
            {activeFields.includes("TIME") && <TableCell>Time (sec)</TableCell>}
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {exercise.sets.map((set, setIndex) => (
            <TableRow key={setIndex}>
              <TableCell sx={{ padding: "5px" }}>
                <Typography>{setIndex + 1}</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography>{set.reps}</Typography>
              </TableCell>
              {activeFields.includes("WEIGHT") && (
                <TableCell>
                  <Field
                    name={`exercises[${index}].sets[${setIndex}].weight`}
                    render={({ input }) => (
                      <TextField
                        name={input.name}
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                        onBlur={handleAutoSave}
                        value={input.value}
                        variant="outlined"
                        size="small"
                        sx={{ width: 77, align: "right" }}
                      />
                    )}
                  />
                </TableCell>
              )}
              {activeFields.includes("RPE") && (
                <TableCell>
                  <Field
                    name={`exercises[${index}].sets[${setIndex}].rpe`}
                    render={({ input }) => (
                      <Select
                        size="small"
                        onChange={(event) => {
                          input.onChange(event);
                          handleAutoSave();
                        }}
                        value={input.value}
                        sx={{ width: 67, "border-color": "green" }}
                      >
                        <MenuItem value={6}>6</MenuItem>
                        <MenuItem value={7}>7</MenuItem>
                        <MenuItem value={8}>8</MenuItem>
                        <MenuItem value={9}>9</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                      </Select>
                    )}
                  />
                </TableCell>
              )}
              {activeFields.includes("TIME") && (
                <TableCell>
                  <Field
                    name={`exercises[${index}].sets[${setIndex}].time`}
                    render={({ input }) => (
                      <TextField
                        name={input.name}
                        type="number"
                        inputProps={{ min: 0 }}
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                        onBlur={handleAutoSave}
                        value={input.value}
                        variant="outlined"
                        size="small"
                        sx={{ width: 90, align: "right" }}
                      />
                    )}
                  />
                </TableCell>
              )}
              <TableCell>
                {isComplete(setIndex) ? (
                  <CheckIcon sx={{ color: "green" }} fontSize="small" />
                ) : (
                  <CloseIcon sx={{ color: "red" }} fontSize="small" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* <Video exerciseIndex={index} /> */}
    </Box>
  );
};

export default Exercise;
